import { useState } from 'react'
import { api, setToken, ApiError, type Account } from '../api'

export function Auth({ onAuthed }: { onAuthed: (a: Account) => void }) {
  const [mode, setMode] = useState<'login' | 'register'>('login')
  // Registration is two-step: fill the form, then enter the emailed code.
  const [step, setStep] = useState<'form' | 'code'>('form')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [sessionKey, setSessionKey] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  function switchMode(next: 'login' | 'register') {
    setMode(next); setStep('form'); setError(null); setCode(''); setSessionKey('')
  }

  async function submitLogin(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      const res = await api.login(email, password)
      setToken(res.accessToken)
      onAuthed(res.account)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Что-то пошло не так.')
    } finally {
      setBusy(false)
    }
  }

  // Register step 1 → request a code.
  async function requestCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      const res = await api.requestCode(email)
      setSessionKey(res.sessionKey)
      setStep('code')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Что-то пошло не так.')
    } finally {
      setBusy(false)
    }
  }

  // Register step 2 → verify the code and create the account.
  async function confirmCode(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setBusy(true)
    try {
      const res = await api.register(email, password, name, sessionKey, code)
      setToken(res.accessToken)
      onAuthed(res.account)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Что-то пошло не так.')
    } finally {
      setBusy(false)
    }
  }

  async function resend() {
    setError(null); setBusy(true)
    try {
      const res = await api.requestCode(email)
      setSessionKey(res.sessionKey)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось отправить код.')
    } finally {
      setBusy(false)
    }
  }

  // ── Code-entry step ──
  if (mode === 'register' && step === 'code') {
    return (
      <div className="auth">
        <h1>Подтвердите email</h1>
        <p className="sub">Мы отправили 6-значный код на <span className="mono">{email}</span>.</p>

        {error && <div className="alert err">{error}</div>}

        <form onSubmit={confirmCode}>
          <div className="field">
            <label>Код из письма</label>
            <input
              className="input mono"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              inputMode="numeric"
              autoFocus
              style={{ fontSize: 22, letterSpacing: 6, textAlign: 'center' }}
              required
            />
          </div>
          <button className="btn primary block" type="submit" disabled={busy || code.length < 6}>
            {busy ? <span className="spin" /> : 'Подтвердить и создать аккаунт'}
          </button>
        </form>

        <div className="switch">
          <button onClick={resend} disabled={busy}>Отправить код заново</button>
          {' · '}
          <button onClick={() => { setStep('form'); setError(null) }}>Изменить данные</button>
        </div>
      </div>
    )
  }

  // ── Login / register form step ──
  return (
    <div className="auth">
      <h1>{mode === 'login' ? 'Вход' : 'Регистрация'}</h1>
      <p className="sub">{mode === 'login' ? 'С возвращением в консоль.' : 'Создайте аккаунт и выпускайте ключи за минуту.'}</p>

      {error && <div className="alert err">{error}</div>}

      <form onSubmit={mode === 'login' ? submitLogin : requestCode}>
        {mode === 'register' && (
          <div className="field">
            <label>Имя <span className="muted">(необязательно)</span></label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Иван" autoComplete="name" />
          </div>
        )}
        <div className="field">
          <label>Email</label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" required />
        </div>
        <div className="field">
          <label>Пароль</label>
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} minLength={8} required />
        </div>
        <button className="btn primary block" type="submit" disabled={busy}>
          {busy ? <span className="spin" /> : mode === 'login' ? 'Войти' : 'Получить код'}
        </button>
      </form>

      <div className="switch">
        {mode === 'login' ? (
          <>Нет аккаунта? <button onClick={() => switchMode('register')}>Зарегистрироваться</button></>
        ) : (
          <>Уже есть аккаунт? <button onClick={() => switchMode('login')}>Войти</button></>
        )}
      </div>
    </div>
  )
}
